/**
 * MeProvider — W3W4 P2-c.
 *
 * Single source of truth for /v1/me state + cross-device settings
 * reconciliation. Mounted at the App.tsx root so consumers (SettingsBody,
 * ProfileDrawer, Lobby) share one cached value.
 *
 * Reconciliation contract (last-write-wins):
 *   - On every successful GET /v1/me, compare server { theme, language,
 *     hapticEnabled, anonymousMode } against the LOCAL settings store
 *     (sourced from localStorage).
 *   - If server has a non-null value AND it differs from local, overwrite
 *     local + apply (theme via applyThemeToDom, lang via i18n.changeLanguage,
 *     etc.) AND emit a toast for USER-VISIBLE settings only (theme/lang/
 *     anonymous; haptic is invisible — skip toast).
 *   - Server NULL = "unset" → no overwrite; client retains its value.
 *
 * Trigger points (per plan §P2-c):
 *   1. Initial app mount (first /v1/me GET).
 *   2. Settings drawer open (caller invokes refresh()).
 *   3. ProfileDrawer open (caller invokes refresh()).
 *   4. Explicit refresh button.
 *
 * NO independent reconciliation in SettingsBody / ProfileDrawer — this
 * provider is the single arbiter.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react'
import { useTranslation } from 'react-i18next'
import type { MeResponse } from '@sagwa/types'
import { apiClient } from '../api/client'
import { useToast } from '../components/useOverlay'
import {
  LS_THEME,
  LS_HAPTIC,
  LS_LANG,
  lsGet,
  lsSet,
  lsBool,
  applyThemeToDom,
  type Theme,
} from '../components/settings/SettingsActions'

// ── Context ────────────────────────────────────────────────────────────────

export interface MeContextValue {
  me: MeResponse | null
  loading: boolean
  error: Error | null
  refresh: () => Promise<void>
}

const MeContext = createContext<MeContextValue | null>(null)

export function useMe(): MeContextValue {
  const ctx = useContext(MeContext)
  if (ctx === null) {
    throw new Error('useMe must be used inside <MeProvider>')
  }
  return ctx
}

// ── Reconciliation ─────────────────────────────────────────────────────────

type ReconcileResult = {
  /** At least one USER-VISIBLE setting was overwritten from server → emit toast. */
  visibleDriftApplied: boolean
}

/**
 * Apply server-side settings onto local state, last-write-wins.
 * Pure-ish (mutates DOM + localStorage + i18n only when drift detected).
 * Returns whether a USER-VISIBLE setting (theme/lang/anonymous) drifted.
 *
 * Exported for unit-test of dirty-check semantics independent of fetch.
 */
export function reconcileSettings(
  serverUser: MeResponse['user'],
  i18n: { changeLanguage: (lang: string) => Promise<unknown> | unknown },
): ReconcileResult {
  let visibleDrift = false

  // Theme
  if (serverUser.theme !== undefined && serverUser.theme !== null) {
    const local = lsGet(LS_THEME, 'auto') as Theme
    if (local !== serverUser.theme) {
      lsSet(LS_THEME, serverUser.theme)
      applyThemeToDom(serverUser.theme)
      visibleDrift = true
    }
  }

  // Language
  if (serverUser.language !== undefined && serverUser.language !== null) {
    const local = lsGet(LS_LANG, '')
    if (local !== serverUser.language) {
      lsSet(LS_LANG, serverUser.language)
      void i18n.changeLanguage(serverUser.language)
      visibleDrift = true
    }
  }

  // Haptic — invisible, no toast contribution.
  if (serverUser.hapticEnabled !== undefined && serverUser.hapticEnabled !== null) {
    const local = lsBool(LS_HAPTIC, true)
    if (local !== serverUser.hapticEnabled) {
      lsSet(LS_HAPTIC, serverUser.hapticEnabled ? '1' : '0')
    }
  }

  // Anonymous mode is sourced from server; no local override exists yet.
  // We surface a visible-drift toast when the cached MeProvider value
  // diverges from the freshly-fetched one (handled by MeProvider state diff).

  return { visibleDriftApplied: visibleDrift }
}

// ── Provider ───────────────────────────────────────────────────────────────

export function MeProvider({ children }: { children: ReactNode }): ReactElement {
  const { i18n } = useTranslation()
  const { showToast } = useToast()
  const [me, setMe] = useState<MeResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const lastAnonymousRef = useRef<boolean | null>(null)

  // Pin i18n + showToast through refs so `refresh` identity is stable across
  // re-renders. Without this, useTranslation/useToast emit new closures every
  // render → refresh changes → useEffect fires → state update → infinite loop.
  const i18nRef = useRef(i18n)
  const showToastRef = useRef(showToast)
  i18nRef.current = i18n
  showToastRef.current = showToast

  const refresh = useCallback(async (): Promise<void> => {
    try {
      const fresh = await apiClient.getMe()
      const prevAnon = lastAnonymousRef.current
      lastAnonymousRef.current = fresh.user.anonymousMode

      const { visibleDriftApplied } = reconcileSettings(fresh.user, i18nRef.current)

      // Anonymous-mode drift (server-only field; tracked via lastAnonymousRef)
      const anonDrift =
        prevAnon !== null && prevAnon !== fresh.user.anonymousMode

      if (visibleDriftApplied || anonDrift) {
        showToastRef.current('info', i18nRef.current.t('settings.synced.fromServer'))
      }

      setMe(fresh)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)))
    }
  }, [])

  // Initial fetch ONLY — refresh is stable so this effect runs once.
  useEffect(() => {
    setLoading(true)
    void refresh().finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <MeContext.Provider value={{ me, loading, error, refresh }}>
      {children}
    </MeContext.Provider>
  )
}

// Re-export for callers that need to mount the provider.
export { MeContext }
